import { useEffect, useState, type KeyboardEvent } from "react";
import type { LucideIcon } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LAUNCH_PAD_UNAVAILABLE_HINT } from "@/components/launch_pad/launchPadConfig";
import { LaunchPadUnavailableHint } from "@/components/launch_pad/LaunchPadUnavailableHint";
import { cn } from "@/lib/utils";
import type { ActiveSection } from "@/types/appNavigation";

type LaunchPadTaskCardProps = {
  section: ActiveSection;
  taskLabel: string;
  description: string;
  icon: LucideIcon;
  onSelect: (section: ActiveSection) => void;
  enabled?: boolean;
  unavailableHint?: string;
};

export function LaunchPadTaskCard({
  section,
  taskLabel,
  description,
  icon: Icon,
  onSelect,
  enabled = true,
  unavailableHint = LAUNCH_PAD_UNAVAILABLE_HINT,
}: LaunchPadTaskCardProps) {
  const [hintVisible, setHintVisible] = useState(false);

  useEffect(() => {
    if (enabled) {
      setHintVisible(false);
      return;
    }

    const media = window.matchMedia("(hover: none)");
    const syncTouchHint = () => setHintVisible(media.matches);
    syncTouchHint();
    media.addEventListener("change", syncTouchHint);
    return () => media.removeEventListener("change", syncTouchHint);
  }, [enabled]);

  const activate = () => {
    if (!enabled) return;
    onSelect(section);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!enabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      activate();
    }
  };

  return (
    <Card
      role={enabled ? "button" : "group"}
      tabIndex={0}
      aria-disabled={enabled ? undefined : true}
      aria-label={!enabled ? `${taskLabel}. ${unavailableHint}` : undefined}
      data-enabled={enabled}
      className={cn(
        "min-w-0 overflow-hidden transition-shadow",
        enabled
          ? "cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          : "group relative border-dashed bg-muted/20 opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      )}
      onClick={enabled ? activate : undefined}
      onKeyDown={enabled ? onKeyDown : undefined}
      onMouseEnter={enabled ? undefined : () => setHintVisible(true)}
      onMouseLeave={enabled ? undefined : () => setHintVisible(false)}
    >
      <CardHeader className="flex flex-row items-start gap-3 space-y-0 p-4">
        <div
          className={cn(
            "shrink-0 rounded-md p-2",
            enabled ? "bg-primary/10" : "bg-muted",
          )}
        >
          <Icon
            className={cn("h-5 w-5", enabled ? "text-primary" : "text-muted-foreground")}
            aria-hidden
          />
        </div>
        <div className="min-w-0 flex-1">
          <CardTitle
            className={cn(
              "text-base leading-snug",
              !enabled && "text-muted-foreground",
            )}
          >
            {taskLabel}
          </CardTitle>
          <CardDescription className="mt-1 line-clamp-2">{description}</CardDescription>
        </div>
      </CardHeader>
      {!enabled && hintVisible ? <LaunchPadUnavailableHint hint={unavailableHint} /> : null}
    </Card>
  );
}
